import { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useReducedMotion } from "motion/react";

import desktopBackgroundUrl from "@/assets/images/onboarding/push-to-remember-desktop-background.png";
import windowsDesktopBackgroundUrl from "@/assets/images/onboarding/push-to-remember-desktop-background-windows.png";
import { MDSLogo, MDSLogoKind } from "@/mds";

interface PushToRememberDesktopGuidanceProps {
  isWindows: boolean;
}

interface AnimationState {
  isCaptionVisible: boolean;
  isHudVisible: boolean;
  isKeyPressed: boolean;
  isNotificationVisible: boolean;
  isRecording: boolean;
  isSent: boolean;
  isZoomed: boolean;
  visibleWordCount: number;
}

const DICTATION_WORDS = "I need to find a new dentist sometime soon.".split(" ");
const DESKTOP_CAMERA_ZOOM = 1.55;
const VOICE_BAR_RANGES = [
  [5, 16],
  [8, 26],
  [8, 30],
  [8, 24],
  [5, 15],
] as const;

const INITIAL_ANIMATION_STATE: AnimationState = {
  isCaptionVisible: true,
  isHudVisible: false,
  isKeyPressed: false,
  isNotificationVisible: false,
  isRecording: false,
  isSent: false,
  isZoomed: false,
  visibleWordCount: 0,
};

const REDUCED_MOTION_STATE: AnimationState = {
  ...INITIAL_ANIMATION_STATE,
  isCaptionVisible: false,
  isNotificationVisible: true,
};

/**
 * Story-ready port of the supplied zooming desktop Push-to-Remember animation.
 * The camera layer leads the eye from the full desktop into Mem's persistent corner orb.
 */
export const PushToRememberDesktopGuidance = ({
  isWindows,
}: PushToRememberDesktopGuidanceProps) => {
  const shouldReduceMotion = useReducedMotion() === true;
  const voiceBarsRef = useRef<HTMLDivElement>(null);
  const [animationState, setAnimationState] = useState<AnimationState>(() =>
    shouldReduceMotion ? REDUCED_MOTION_STATE : INITIAL_ANIMATION_STATE
  );

  useEffect(() => {
    if (!animationState.isRecording || voiceBarsRef.current == null) {
      return;
    }

    const bars = Array.from(voiceBarsRef.current.querySelectorAll<HTMLSpanElement>("span"));
    const oscillators = bars.map(() => ({
      frequency: 9 + Math.random() * 7,
      phase: Math.random() * Math.PI * 2,
    }));
    const startedAt = performance.now();
    let animationFrame: number;
    let energy = 0.35;
    let targetEnergy = 0.7;
    let nextRetargetAt = 0;

    const animateBars = (now: number) => {
      const elapsedSeconds = (now - startedAt) / 1000;

      if (now >= nextRetargetAt) {
        nextRetargetAt = now + 200 + Math.random() * 200;
        const randomValue = Math.random();

        targetEnergy =
          randomValue < 0.13
            ? 0.05 + Math.random() * 0.12
            : randomValue < 0.6
              ? 0.35 + Math.random() * 0.3
              : 0.72 + Math.random() * 0.28;
      }

      energy += (targetEnergy - energy) * 0.1;

      bars.forEach((bar, index) => {
        const [minimumHeight, maximumHeight] = VOICE_BAR_RANGES[index];
        const oscillator = oscillators[index];
        const wobble =
          0.6 +
          0.4 * Math.sin(elapsedSeconds * oscillator.frequency + oscillator.phase);
        const scaledEnergy = Math.min(1, Math.max(0, energy * wobble + 0.06));

        bar.style.height = `${(
          minimumHeight +
          (maximumHeight - minimumHeight) * scaledEnergy
        ).toFixed(1)}px`;
      });

      animationFrame = requestAnimationFrame(animateBars);
    };

    animationFrame = requestAnimationFrame(animateBars);

    return () => cancelAnimationFrame(animationFrame);
  }, [animationState.isRecording]);

  useEffect(() => {
    if (shouldReduceMotion) {
      setAnimationState(REDUCED_MOTION_STATE);
      return;
    }

    let timeouts: Array<ReturnType<typeof setTimeout>> = [];

    const schedule = (delayMs: number, update: Partial<AnimationState> | (() => void)) => {
      const timeout = setTimeout(() => {
        if (typeof update === "function") {
          update();
          return;
        }

        setAnimationState(previousState => ({ ...previousState, ...update }));
      }, delayMs);

      timeouts.push(timeout);
    };

    const runScene = () => {
      setAnimationState(INITIAL_ANIMATION_STATE);

      schedule(500, { isZoomed: true });
      schedule(2300, { isHudVisible: true });
      schedule(3050, { isKeyPressed: true, isRecording: true });

      DICTATION_WORDS.forEach((_word, wordIndex) => {
        schedule(3500 + wordIndex * 175, { visibleWordCount: wordIndex + 1 });
      });

      const dictationCompleteAt = 3500 + DICTATION_WORDS.length * 175;

      schedule(dictationCompleteAt + 350, { isKeyPressed: false });
      schedule(dictationCompleteAt + 500, { isRecording: false, isSent: true });
      schedule(dictationCompleteAt + 1100, { isHudVisible: false });
      schedule(dictationCompleteAt + 1700, { isCaptionVisible: false });
      schedule(dictationCompleteAt + 2400, { isSent: false });
      schedule(dictationCompleteAt + 2950, { isNotificationVisible: true });
      schedule(dictationCompleteAt + 5900, { isNotificationVisible: false });
      schedule(dictationCompleteAt + 6250, { isZoomed: false });
      schedule(dictationCompleteAt + 8300, runScene);
    };

    runScene();

    return () => {
      timeouts.forEach(clearTimeout);
      timeouts = [];
    };
  }, [shouldReduceMotion]);

  return (
    <DesktopStage aria-hidden="true" data-push-to-remember-guidance>
      <DesktopWorld $isWindows={isWindows} $isZoomed={animationState.isZoomed}>
        <AgentNotification
          $isWindows={isWindows}
          $isVisible={animationState.isNotificationVisible}
        >
          Got it — I&apos;ll check back in a few days about finding a dentist. 🦷
        </AgentNotification>

        <AgentOrbAnchor $isWindows={isWindows}>
          <AgentOrb>
            <OrbRing $isVisible={animationState.isRecording} aria-hidden="true">
              <svg viewBox="0 0 74 74">
                <circle cx="37" cy="37" r="35" />
              </svg>
            </OrbRing>
            <OrbDisc $isRecording={animationState.isRecording} />
            <OrbLogo
              $isVisible={!animationState.isRecording && !animationState.isSent}
              kind={MDSLogoKind.Arrows}
            />
            <VoiceBars
              ref={voiceBarsRef}
              $isVisible={animationState.isRecording}
              aria-hidden="true"
            >
              <span />
              <span />
              <span />
              <span />
              <span />
            </VoiceBars>
            <SentCheck $isVisible={animationState.isSent} aria-hidden="true" viewBox="0 0 32 32">
              <path d="M7 17 L13.5 23.5 L25 9.5" />
            </SentCheck>
          </AgentOrb>
        </AgentOrbAnchor>
      </DesktopWorld>

      <DictationCaption $isVisible={animationState.isCaptionVisible}>
        {DICTATION_WORDS.map((word, wordIndex) => (
          <DictationWord
            key={`${word}-${wordIndex}`}
            $isVisible={wordIndex < animationState.visibleWordCount}
          >
            {word}
          </DictationWord>
        ))}
      </DictationCaption>

      <ShortcutHud $isVisible={animationState.isHudVisible}>
        <Keycap $isPressed={animationState.isKeyPressed} $isWindows={isWindows}>
          {isWindows ? "Ctrl" : "⌥"}
        </Keycap>
        <ShortcutLabel>{isWindows ? "Right Ctrl" : "Right Option"}</ShortcutLabel>
      </ShortcutHud>
    </DesktopStage>
  );
};

const DesktopStage = styled.div`
  position: relative;
  width: min(100%, 420px);
  aspect-ratio: 420 / 288;
  margin-inline: auto;
  flex: none;
  overflow: hidden;
  border-radius: 10px;
`;

const DesktopWorld = styled.div<{ $isWindows: boolean; $isZoomed: boolean }>`
  position: absolute;
  inset: 0;
  transform: scale(
    ${({ $isWindows, $isZoomed }) =>
      $isZoomed ? DESKTOP_CAMERA_ZOOM : $isWindows ? 1.2 : 1}
  );
  transform-origin: 100% 100%;
  background: url(${({ $isWindows }) =>
      $isWindows ? windowsDesktopBackgroundUrl : desktopBackgroundUrl})
    center / cover no-repeat;
  transition: transform 1300ms cubic-bezier(0.55, 0.05, 0.15, 1);
  will-change: transform;
`;

const DictationCaption = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 13%;
  left: 50%;
  display: flex;
  width: 88%;
  flex-wrap: wrap;
  justify-content: center;
  column-gap: 0.26em;
  transform: translateX(-50%);
  color: white;
  font-size: 18px;
  font-weight: 580;
  letter-spacing: -0.005em;
  line-height: 1.4;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  text-align: center;
  text-shadow:
    0 1px 3px rgb(0 0 0 / 35%),
    0 4px 18px rgb(0 0 0 / 35%);
  transition: opacity 450ms ease;
`;

const DictationWord = styled.span<{ $isVisible: boolean }>`
  display: inline-block;
  transform: scale(${({ $isVisible }) => ($isVisible ? 1 : 0.88)});
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition:
    opacity 160ms ease-out,
    transform 200ms cubic-bezier(0.3, 1.3, 0.5, 1);
`;

const ShortcutHud = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 36%;
  left: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 26px 16px;
  transform: translate(-50%, ${({ $isVisible }) => ($isVisible ? "0" : "12px")})
    scale(${({ $isVisible }) => ($isVisible ? 1 : 0.95)});
  border: 1px solid rgb(255 255 255 / 14%);
  border-radius: 22px;
  background: rgb(26 26 36 / 52%);
  box-shadow: 0 18px 44px rgb(0 0 0 / 35%);
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  backdrop-filter: blur(16px) saturate(1.2);
  transition:
    opacity 320ms ease,
    transform 380ms cubic-bezier(0.25, 1.25, 0.45, 1);
`;

const Keycap = styled.div<{ $isPressed: boolean; $isWindows: boolean }>`
  display: flex;
  width: 78px;
  height: 70px;
  align-items: center;
  justify-content: center;
  transform: translateY(${({ $isPressed }) => ($isPressed ? "4px" : "0")});
  border: 1.5px solid #dcdce6;
  border-radius: 14px;
  background: ${({ $isPressed }) => ($isPressed ? "#e6e6ee" : "white")};
  box-shadow: ${({ $isPressed }) => ($isPressed ? "0 1px 0 #d8d8e2" : "0 5px 0 #d8d8e2")};
  color: #5f5f78;
  font-size: ${({ $isWindows }) => ($isWindows ? "19px" : "26px")};
  font-weight: 600;
  line-height: 1;
  transition:
    transform 100ms ease,
    background 100ms ease,
    box-shadow 100ms ease;
  user-select: none;
`;

const ShortcutLabel = styled.div`
  margin-top: 19px;
  color: rgb(255 255 255 / 90%);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1;
  white-space: nowrap;
`;

const AgentNotification = styled.div<{ $isVisible: boolean; $isWindows: boolean }>`
  position: absolute;
  right: 8px;
  bottom: calc(${({ $isWindows }) => ($isWindows ? "21px" : "7px")} + 64px * 0.62 + 10px);
  max-width: 148px;
  padding: 8px 10px;
  transform: scale(${({ $isVisible }) => ($isVisible ? 1 : 0.55)});
  transform-origin: 100% 100%;
  border-radius: 11px;
  border-bottom-right-radius: 0;
  background: #f4f2ee;
  box-shadow: 0 8px 20px rgb(0 0 0 / 32%);
  color: #3d3d47;
  font-size: 8.5px;
  font-weight: 500;
  letter-spacing: -0.016em;
  line-height: 1.5;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition:
    opacity 220ms ease,
    transform 340ms cubic-bezier(0.25, 1.35, 0.45, 1);
`;

const AgentOrbAnchor = styled.div<{ $isWindows: boolean }>`
  position: absolute;
  right: 10px;
  bottom: ${({ $isWindows }) => ($isWindows ? "21px" : "7px")};
  width: 64px;
  aspect-ratio: 1;
  transform: scale(0.62);
  transform-origin: 100% 100%;
`;

const AgentOrb = styled.div`
  position: absolute;
  inset: 0;
  aspect-ratio: 1;
`;

const OrbDisc = styled.div<{ $isRecording: boolean }>`
  position: absolute;
  inset: 4px;
  border-radius: 50%;
  transform: scale(${({ $isRecording }) => ($isRecording ? 1.06 : 1)});
  background: ${({ $isRecording }) => ($isRecording ? "#7c8ef5" : "#ed7458")};
  box-shadow: 0 6px 20px rgb(0 0 0 / 35%);
  transition:
    background 320ms ease,
    transform 300ms cubic-bezier(0.3, 1.3, 0.5, 1);
`;

const OrbLogo = styled(MDSLogo)<{ $isVisible: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 47%;
  height: auto;
  transform: translate(-50%, -50%) scale(${({ $isVisible }) => ($isVisible ? 1 : 0.6)});
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition:
    opacity 200ms ease,
    transform 250ms cubic-bezier(0.3, 1.3, 0.5, 1);

  path {
    fill: white;
  }
`;

const OrbRing = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  inset: -5px;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  animation: spin 1.15s linear infinite;
  animation-play-state: ${({ $isVisible }) => ($isVisible ? "running" : "paused")};
  transition: opacity 250ms ease;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  svg {
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
  }

  circle {
    fill: none;
    stroke: #aab7ff;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-dasharray: 58 165;
    opacity: 0.85;
  }
`;

const VoiceBars = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 180ms ease;

  span {
    width: 4px;
    height: 8px;
    border-radius: 2px;
    background: white;
  }
`;

const SentCheck = styled.svg<{ $isVisible: boolean }>`
  position: absolute;
  inset: 25%;
  width: 50%;
  height: 50%;
  transform: scale(${({ $isVisible }) => ($isVisible ? 1 : 0.65)});
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition:
    opacity 180ms ease,
    transform 260ms cubic-bezier(0.3, 1.3, 0.5, 1);

  path {
    fill: none;
    stroke: white;
    stroke-width: 3.4;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 34;
    stroke-dashoffset: ${({ $isVisible }) => ($isVisible ? 0 : 34)};
    transition: stroke-dashoffset 380ms ease 220ms;
  }
`;
